import React, { forwardRef } from "react";
import { Crown, Sparkles, Users, Target, Trophy, Zap, Heart } from "lucide-react";

const CATEGORY_LABELS = {
  music: "Music", movie: "Movies", sports: "Sports",
  historical: "Historical", business: "Business", mixed: "Mixed",
};

const YEAR = new Date().getFullYear();

/**
 * PrimeWrapped — Spotify-Wrapped-style share card for the current user.
 * Rendered as a fixed 1080x1350 (4:5) card ideal for social sharing.
 * `ref` is forwarded so the parent can convert this DOM to PNG via html-to-image.
 */
export const PrimeWrapped = forwardRef(({ user, wrapped }, ref) => {
  if (!user || !wrapped) return null;

  const bestCat = wrapped.best_category;
  const bestFriend = wrapped.best_friend;
  const hardest = wrapped.hardest_prediction;
  const best = wrapped.best_single;

  return (
    <div
      ref={ref}
      data-testid="wrapped-card"
      className="relative overflow-hidden rounded-[36px]"
      style={{
        width: 1080 / 2.5,
        height: 1350 / 2.5,
        background:
          "radial-gradient(circle at 20% 5%, rgba(255,16,122,0.55), transparent 55%)," +
          "radial-gradient(circle at 90% 100%, rgba(0,229,255,0.35), transparent 55%)," +
          "linear-gradient(160deg, #16141D 0%, #0A090D 100%)",
        color: "#FDFBF7",
        fontFamily: "'Outfit', ui-sans-serif, system-ui",
      }}
    >
      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative h-full flex flex-col p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[9px] tracking-[0.4em] uppercase font-bold text-[#FF107A]">
              Celebrity Smash
            </div>
            <div
              className="font-display text-[42px] leading-[0.85] mt-1"
              style={{ fontFamily: "'Bebas Neue', 'Outfit', sans-serif", letterSpacing: "-0.02em" }}
            >
              Prime<br />Wrapped<br />
              <span className="text-[#FF107A]">{YEAR}</span>
            </div>
          </div>
          <div
            className="w-16 h-16 rounded-2xl border-2 border-[#FFD700]/60 flex items-center justify-center"
            style={{ boxShadow: "0 0 24px rgba(255,215,0,0.35)" }}
          >
            <Crown size={30} className="text-[#FFD700]" />
          </div>
        </div>

        {/* Identity */}
        <div className="mt-5 flex items-center gap-3">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-12 h-12 rounded-xl border-2 border-[#FF107A]/70" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-[#FF107A]/20 border-2 border-[#FF107A]/70 flex items-center justify-center font-display text-2xl text-[#FF107A]">
              {user.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display text-2xl leading-none truncate">{user.name}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FFD700] mt-1 font-bold">
              {wrapped.title}
            </div>
          </div>
        </div>

        {/* Hero stat */}
        <div
          className="mt-5 rounded-3xl border border-white/10 p-5 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,16,122,0.25) 0%, rgba(168,85,247,0.15) 100%)",
          }}
        >
          <div className="text-[9px] tracking-[0.35em] uppercase font-bold text-[#FDFBF7]/70">
            Prime Rating
          </div>
          <div
            className="font-display text-[72px] leading-[0.85] mt-1 tabular"
            style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#FFD700" }}
          >
            {wrapped.prime_rating}
          </div>
          <div className="mt-2 text-sm text-[#FDFBF7]/80">
            Ranked <span className="font-bold text-[#FDFBF7]">#{wrapped.global_rank}</span> of{" "}
            {wrapped.total_users}
          </div>
        </div>

        {/* Grid stats */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <MiniStat label="Predicted" value={wrapped.people_predicted} icon={Users} accent="#00E5FF" />
          <MiniStat label="Accuracy" value={`${wrapped.accuracy}%`} icon={Target} accent="#FF107A" />
          <MiniStat label="Perfect" value={wrapped.perfect_predictions} icon={Zap} accent="#A855F7" />
        </div>

        {/* Highlights */}
        <div className="mt-4 space-y-2 flex-1">
          {bestCat && (
            <HighlightRow
              accent="#00E5FF"
              icon={Sparkles}
              label="Best Category"
              title={CATEGORY_LABELS[bestCat.category] || bestCat.category}
              detail={`${bestCat.accuracy}% accuracy · ${bestCat.won}/${bestCat.played}`}
              testId="wrapped-best-cat"
            />
          )}
          {bestFriend && (
            <HighlightRow
              accent="#FF107A"
              icon={Heart}
              label="Best Friend"
              title={bestFriend.name}
              detail={`${bestFriend.primes} primes across ${bestFriend.played} smashes`}
              testId="wrapped-best-friend"
            />
          )}
          {hardest && (
            <HighlightRow
              accent="#A855F7"
              icon={Target}
              label="Hardest Prediction"
              title={hardest.receiver_name || "Mystery player"}
              detail={`Confidence ${hardest.confidence}/10 · unlucky`}
              testId="wrapped-hardest"
            />
          )}
          {best && (
            <HighlightRow
              accent="#FFD700"
              icon={Trophy}
              label="Biggest Win"
              title={best.classification || "Legend"}
              detail={`${best.score} points · ${best.receiver_name || "solo run"}`}
              testId="wrapped-best-single"
            />
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between pt-3 border-t border-white/10">
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#FDFBF7]/60">
            celebritysmash.app
          </div>
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#FF107A] font-bold">
            #PrimeWrapped
          </div>
        </div>
      </div>
    </div>
  );
});
PrimeWrapped.displayName = "PrimeWrapped";

const MiniStat = ({ label, value, icon: Icon, accent }) => (
  <div
    className="rounded-2xl border border-white/10 bg-black/40 px-2 py-3 text-center"
    style={{ boxShadow: `inset 0 0 12px ${accent}22` }}
  >
    <Icon size={12} className="mx-auto mb-1" style={{ color: accent }} />
    <div className="font-display text-2xl leading-none tabular" style={{ color: accent }}>
      {value}
    </div>
    <div className="text-[8px] tracking-[0.25em] uppercase text-[#FDFBF7]/60 mt-1">{label}</div>
  </div>
);

const HighlightRow = ({ accent, icon: Icon, label, title, detail, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-white/10 bg-[#0A090D]/70 px-3 py-2.5 flex items-center gap-3"
  >
    <div
      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
      style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}
    >
      <Icon size={16} style={{ color: accent }} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[9px] tracking-[0.3em] uppercase font-bold" style={{ color: accent }}>
        {label}
      </div>
      <div className="font-display text-lg leading-none mt-0.5 truncate">{title}</div>
      <div className="text-[10px] text-[#FDFBF7]/60 truncate">{detail}</div>
    </div>
  </div>
);
